<template>
    <b-modal
        v-model="modal"
        :id="'assignment-add-profile-card-modal-' + part.id"
        :ref="'assignment-add-profile-card-modal-' + part.id"
        :title="$t('assignment.add_part_to_profile')"
        :ok-title="$t('common.ok')"
        :cancel-title="$t('common.cancel')"
        cancel-variant="outline-primary"
        size="md"
        @ok="onOk"
        :ok-disabled="disabled"
        modal-class="apprentice-default-modal"
        :no-enforce-focus="true">
        <div class="add-to-profile">
            <div class="title">
                <b-form-group
                    :label="$t('common.title')"
                    label-for="add-to-profile-title-input">
                    <b-form-input
                        id="add-to-profile-title-input"
                        v-model="title"
                        :placeholder="$t('profile.enter_a_title')"></b-form-input>
                </b-form-group>
            </div>

            <div class="description">
                <b-form-group
                    :label="$t('common.description')"
                    label-for="add-to-profile-description-input">
                    <ckeditor
                        id="add-to-profile-description-input"
                        v-model="description"
                        @ready="onReady"
                        :editor="editor"
                        :config="editorConfig"></ckeditor>
                </b-form-group>
            </div>

            <div class="category">
                <b-form-group
                    :label="$t('assignment.category')"
                    label-for="add-to-profile-category-input">
                    <b-form-select
                        class="apprentice-primary-select"
                        id="add-to-profile-category-input"
                        v-model="selectedCategory"
                        :options="profileCategoriesIn"
                        text-field="title"
                        value-field="id">
                    </b-form-select>
                </b-form-group>
            </div>

            <div class="date">
                <b-form-group
                    :label="$t('assignment.date')"
                    label-for="add-to-profile-date-input">
                    <b-form-datepicker
                        class="apprentice-date-picker btn-date-group"
                        id="add-to-profile-date-input"
                        v-model="date"
                        start-weekday="1"
                        now-button
                        reset-button
                        right
                        today-variant="primary"
                        nav-button-variant="primary"
                        locale="nl"
                        :aria-controls="$t('assignment.choose_date')"
                        label-reset-button="reset"
                        :label-no-date-selected="$t('assignment.no_date_selected')"
                        menu-class="datedropdown"
                        :date-format-options="{ year: 'numeric', month: 'short', day: '2-digit', weekday: 'short' }">
                    </b-form-datepicker>
                </b-form-group>
            </div>

            <div
                class="files"
                v-if="this.part.sub_type === 2">
                <div class="component-title">
                    <h5 class="title">{{ $t('assignment.files') }}</h5>
                </div>

                <div
                    class="file"
                    v-for="file of files"
                    v-if="files.length > 0">
                    <div
                        class="icon icon-type"
                        :class="getFileType(file.extensions)"></div>
                    <div class="title">
                        {{ file.full_name }}
                        <div class="file-size text-muted">
                            {{ file.size }}
                        </div>
                    </div>
                    <div class="remove-file">
                        <b-button
                            class="btn-remove btn-remove-attached-file"
                            variant="danger-icon-only"
                            @click="removeFile(file)">
                        </b-button>
                    </div>
                </div>
            </div>

            <div
                class="attachments"
                v-else-if="part.sub_type === 4">
                <div class="not-found with-header attachments-list">
                    <div class="icon">
                        <i class="fas fa-exclamation-circle"></i>
                    </div>
                    <div class="alert-notice">{{ $t('assignment.add_report_attachment') }}</div>
                </div>
            </div>
        </div>
    </b-modal>
</template>

<script>
import moment from 'moment';
import ApprenticeEditor from '../../../../../helpers/ApprenticeEditor';

export default {
    name: 'design-2021-assignment-show-add-to-profile-modal-component',
    props: ['part', 'projectId', 'profileCategoriesIn', 'isCoopIn'],
    data() {
        return {
            title: this.part.title ? this.part.title : this.$t('assignment.question') + ' ' + this.part.getNameOrder(),
            description: this.createDescription(this.part) !== null ? this.createDescription(this.part) : '',
            selectedCategory: this.profileCategoriesIn.length ? this.profileCategoriesIn[0].id : null,
            date: null,
            datepickerOptionsDate: {
                format: 'DD-MM-YYYY',
                useCurrent: false,
            },
            editor: ApprenticeEditor,
            editorConfig: {
                contentsCss: 'test',
                bodyClass: 'mybodyclass',
                toolbar: {
                    items: [
                        'undo',
                        'redo',
                        '|',
                        'heading',
                        '|',
                        'bold',
                        'italic',
                        'underline',
                        '|',
                        'link',
                        'bulletedList',
                        'numberedList',
                        '|',
                        'outdent',
                        'indent',
                        'alignment',
                        '|',
                        'imageInsert',
                        'mediaEmbed',
                        'insertTable',
                        '|',
                        'fontColor',
                        'fontFamily',
                        'fontSize',
                        'fontBackgroundColor',
                        'highlight',
                        'specialCharacters',
                        '|',
                        'horizontalLine',
                    ],
                },
                language: this.$authUser.locale,
                table: {
                    contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells'],
                },
            },
            modal: false,
            file: null,
            url: null,
            filesLoading: false,
            files: !this.part.answers.length
                ? []
                : this.part.answers[0].files.map((file) => {
                      return file.file;
                  }),
        };
    },
    watch: {
        part: function (part) {
            this.title = part.title;
            this.files = !part.answers.length
                ? []
                : part.answers[0].files.map((file) => {
                      return file.file;
                  });
        },
    },
    computed: {
        disabled() {
            return !this.title || !this.selectedCategory || !this.date;
        },
        largeModal() {
            return this.part.sub_type === 2 || this.part.sub_type === 4;
        },
    },
    methods: {
        onOk() {
            const data = {
                title: this.title,
                description: this.description,
                category_id: this.selectedCategory,
                date: moment(this.date).format('DD-MM-YYYY'),
                file_ids: this.files.map((file) => file.id),
                project_id: this.projectId,
                part_id: this.part.id,
                coop: this.isCoopIn,
            };

            this.alert.saving();
            this.axios.post(this.routes.assignmentPartAddToProfile(), data).then(
                (response) => {
                    this.alert.success(this.$t('assignment.added'), this.$t('assignment.successfully_added'));
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },
        createDescription(part) {
            if (!part.isAssignmentText() && !part.isAssignmentQuestionAndAnswer()) {
                return '';
            }

            let description = '';

            if (part.text.value) {
                description += '<p><strong>' + part.text.value + '</strong></p>';
            } else if (part.hasAnswer()) {
                description += '<p>' + part.getAnswer().text.value + '</p>';
            }
            if (part.getAnswer()) {
                description += '<p>' + part.getAnswer().text.value + '</p>';
            }
            if (part.getAnswer()) {
                description += '<p>' + part.getAnswer().text.value + '</p>';
            }

            return description;
        },

        uploadFilesChanged() {
            this.filesLoading = true;
            this.alert.saving();
            const files = this.$refs.fileUpload.files;

            let data = new FormData();
            let count = 0;
            for (const file of files) {
                data.append('files[' + count + ']', file);
                count++;
            }
            const headers = { 'Content-Type': 'multipart/form-data' };

            this.axios.post(this.routes.profileCardFiles(this.card.id), data, { headers: headers }).then(
                (response) => {
                    for (const f of response.data) {
                        this.card.profile_card_files.push(f);
                    }
                    this.filesLoading = false;
                    this.alert.success();
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },
        onSubmitLink() {
            this.alert.saving();

            const data = {
                url: this.url,
            };

            this.axios.post(this.routes.profileCardVideoUrl(this.card.id), data).then(
                (response) => {
                    this.card.video_links.push(response.data);
                    this.url = null;
                    this.alert.success();
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },
        removeLink(link) {
            this.alert
                .confirm(this.$t('assignment.delete_link'), this.$t('assignment.delete_link_confirm'))
                .then((confirm) => {
                    if (!confirm) {
                        return;
                    }
                    this.alert.saving();
                    this.axios.delete(this.routes.profileCardVideoUrl(link.id)).then(
                        (response) => {
                            this.card.video_links = this.card.video_links.filter((l) => l.id !== link.id);
                            this.alert.success();
                        },
                        (error) => {
                            this.alert.ajaxError(error);
                        },
                    );
                });
        },
        removeFile(file) {
            this.files = this.files.filter((f) => f.id !== file.id);
        },
        isImage(file) {
            const extenions = [
                'jpg',
                'JPG',
                'jpeg',
                'JPEG',
                'png',
                'PNG',
                'gif',
                'GIF',
                'tiff',
                'TIFF',
                'bmp',
                'BMP',
                'jif',
                'jfif',
            ];
            for (const ext of extenions) {
                if (ext === file.extension) {
                    return true;
                }
            }
            return false;
        },
        isVideo(file) {
            return file.extension === 'mp4';
        },

        getFileType(filetype) {
            switch (filetype) {
                case 'jpeg':
                case 'jpg':
                case 'png':
                case 'jif':
                case 'jfif':
                case 'gif':
                    return 'btn-image-type ';

                case 'pdf':
                    return 'btn-file-type ';

                case 'doc':
                case 'docx':
                    return 'btn-doc-type ';

                case 'ppt':
                case 'pptx':
                    return 'btn-doc-type ';

                case 'xls':
                case 'xlx':
                    return 'btn-doc-type ';

                case 'cda':
                case 'mp3':
                case 'oga':
                    return 'btn-audio-type ';

                case 'mov':
                case 'mpeg':
                case 'mkv':
                case 'ogv':
                case 'webm':
                case '3gp':
                    return 'btn-video-type ';

                default:
                    return 'btn-file-type ';
            }
        },

        onReady() {
            const wrapper = document.querySelector('.ck-body-wrapper');
            wrapper.remove();
            const editor = document.querySelector('.ck-editor');
            editor.appendChild(wrapper);
        },
    },
};
</script>

<style scoped lang="scss">
.files {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    font-size: 1.3125rem;

    .component-title {
        flex: 1 0 100%;
        margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');
    }

    .file {
        display: flex;
        justify-content: flex-start;
        flex-wrap: wrap;
        align-items: center;
        flex: 1;

        &:not(:last-child) {
            margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
        }
    }

    .icon {
        align-self: center;
        padding: 10px;
        background: $secondary-background-color;
        border-radius: $border-radius-content;
        color: var(--primary);
        border: 1px solid var(--primary);
        font-size: 1.3125rem;
    }

    .title {
        font-size: $base-font-size;
        font-family: $headings-font-family;
        font-weight: $font-weight-bold;
        margin-left: map-get($grid-gutter-spacing, 'ten');
        margin-right: map-get($grid-gutter-spacing, 'ten');
        align-self: center;
        word-break: break-word;

        .file-size {
            font-size: 0.75rem;
            font-weight: $font-weight-normal;
        }
    }

    .remove-file {
        margin-left: auto;

        .btn {
            font-size: 1.3125rem;
        }
    }
}
</style>
